import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './store';
import './App.css';

import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { UsersPage } from './pages/UsersPage';
import { StockPage } from './pages/StockPage';
import { TransactionsPage } from './pages/TransactionsPage';
import { ReportsPage } from './pages/ReportsPage';
import { BranchesPage } from './pages/BranchesPage';
import { BranchConfigPage } from './pages/BranchConfigPage';
import { StockHistoryPage } from './pages/StockHistoryPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';

// ── NavLink ──
interface NavLinkProps {
  to: string;
  icon: string;
  label: string;
  onClick?: () => void;
}

const NavLink: React.FC<NavLinkProps> = ({ to, icon, label, onClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <button
      className={`nav-link${isActive ? ' active' : ''}`}
      onClick={() => { navigate(to); onClick?.(); }}
    >
      <span>{icon}</span> {label}
    </button>
  );
};

// ── Sidebar ──
interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const { user } = useAuth();

  return (
    <>
      {/* Overlay — mobile only */}
      <div className={`sidebar-overlay${open ? ' open' : ''}`} onClick={onClose} />

      <aside className={`sidebar${open ? ' open' : ''}`}>
        <div className="sidebar-logo">
          <img src="/logo.png" alt="B Pos" style={{ height: 32, objectFit: 'contain' }} />
          <span>B Pos</span>
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/dashboard" icon="📊" label="Dashboard"       onClick={onClose} />
          <NavLink to="/users"     icon="👥" label="Users"           onClick={onClose} />
          <NavLink to="/stock"     icon="📦" label="Add-Ons & Stok"  onClick={onClose} />
          {['super_admin', 'finance', 'accounting'].includes(user?.role || '') && (
            <NavLink to="/stock-history" icon="🗂️" label="Monitoring Stok" onClick={onClose} />
          )}
          <NavLink to="/transactions" icon="💳" label="Transactions" onClick={onClose} />
          <NavLink to="/reports"   icon="📈" label="Reports"         onClick={onClose} />
          <NavLink to="/branches"  icon="🏢" label="Branches"        onClick={onClose} />
          <NavLink to="/branch-config" icon="⚙️" label="Branch Config" onClick={onClose} />
          {['super_admin', 'finance', 'accounting'].includes(user?.role || '') && (
            <NavLink to="/notification-settings" icon="🔔" label="Notifikasi" onClick={onClose} />
          )}
        </nav>

        <div className="sidebar-footer">
          <p className="sidebar-user">{user?.name || 'User'}</p>
          <p className="sidebar-role">{user?.role?.replace('_', ' ')}</p>
        </div>
      </aside>
    </>
  );
};

// ── Main layout (needs to be inside Router for hooks) ──
const AppLayout: React.FC = () => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="app-container">
      {/* Mobile top bar */}
      <div className="mobile-topbar">
        <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)} aria-label="Toggle menu">
          <span />
          <span />
          <span />
        </button>
        <span className="mobile-topbar-title">B Pos</span>
      </div>

      <Sidebar open={sidebarOpen} onClose={closeSidebar} />

      <main className="content">
        <Routes>
          <Route path="/dashboard"    element={<DashboardPage />} />
          <Route path="/users"        element={<UsersPage />} />
          <Route path="/stock"        element={<StockPage />} />
          <Route path="/stock-history" element={['super_admin', 'finance', 'accounting'].includes(user?.role || '') ? <StockHistoryPage /> : <Navigate to="/dashboard" replace />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/reports"      element={<ReportsPage />} />
          <Route path="/branches"     element={<BranchesPage />} />
          <Route path="/branch-config" element={<BranchConfigPage />} />
          <Route path="/notification-settings" element={['super_admin', 'finance', 'accounting'].includes(user?.role || '') ? <NotificationSettingsPage /> : <Navigate to="/dashboard" replace />} />
          <Route path="*"             element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
}

export default App;
